import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format, parseISO } from 'date-fns';

/**
 * Component to display partnership performance charts
 */
const PartnershipPerformanceChart = ({ performanceData, isLoading, selectedPartnership }) => {
  const [chartType, setChartType] = useState('chemistry'); // 'chemistry' or 'performance'

  // Process data for charts
  const chartData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];

    // Group data by date and partnership
    const groupedData = {};
    
    performanceData.forEach(point => {
      const date = format(parseISO(point.date), 'MMM dd');
      const key = `${date}_${point.partnershipId}`;
      
      if (!groupedData[key]) {
        groupedData[key] = {
          date,
          partnershipId: point.partnershipId,
          partnerId: point.partnerId,
          chemistryScore: point.chemistryScore,
          performanceVsExpected: point.performanceVsExpected,
          wins: 0,
          losses: 0,
          totalGames: 0,
          avgExpectedWinProb: 0,
          expectedWinProbs: []
        };
      }
      
      // Update statistics
      groupedData[key].totalGames++;
      groupedData[key].expectedWinProbs.push(point.expectedWinProbability);
      
      if (point.result === 'win') {
        groupedData[key].wins++;
      } else {
        groupedData[key].losses++;
      }
    });

    // Calculate averages and convert to array
    return Object.values(groupedData).map(item => ({
      ...item,
      winRate: item.totalGames > 0 ? (item.wins / item.totalGames) * 100 : 0,
      avgExpectedWinProb: item.expectedWinProbs.length > 0 
        ? item.expectedWinProbs.reduce((sum, prob) => sum + prob, 0) / item.expectedWinProbs.length 
        : 0,
      performanceVsExpected: item.performanceVsExpected * 100 // Convert to percentage
    })).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [performanceData]);

  // Filter data for selected partnership if any
  const filteredData = useMemo(() => {
    if (!selectedPartnership) return chartData;
    return chartData.filter(item => item.partnershipId === selectedPartnership.id);
  }, [chartData, selectedPartnership]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface p-4 border border-border-medium rounded-lg shadow-lg">
          <p className="font-medium text-text-primary mb-2">{label}</p>
          {chartType === 'chemistry' ? (
            <div className="space-y-1">
              <p className="text-sm text-text-primary">
                <span className="text-info">Chemistry Score:</span> {data.chemistryScore.toFixed(2)}
              </p>
              <p className="text-sm text-text-primary">
                <span className="text-success-dark">Win Rate:</span> {data.winRate.toFixed(1)}%
              </p>
              <p className="text-sm text-text-secondary">Games: {data.totalGames}</p>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-sm text-text-primary">
                <span className="text-info">Performance vs Expected:</span> {data.performanceVsExpected.toFixed(1)}%
              </p>
              <p className="text-sm text-text-primary">
                <span className="text-warning-dark">Expected Win Rate:</span> {(data.avgExpectedWinProb * 100).toFixed(1)}%
              </p>
              <p className="text-sm text-text-primary">
                <span className="text-success-dark">Actual Win Rate:</span> {data.winRate.toFixed(1)}%
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">📈 Partnership Performance</h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!performanceData || performanceData.length === 0) {
    return (
      <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold mb-4 text-text-primary">📈 Partnership Performance</h2>
        <div className="flex justify-center items-center h-64 bg-surface-subtle rounded-lg">
          <div className="text-center">
            <p className="text-text-muted mb-2">No partnership performance data</p>
            <p className="text-sm text-text-muted">Play more games with partners to see performance trends</p>
          </div>
        </div>
      </div>
    );
  }

  const chartTheme = typeof document !== 'undefined' ? (() => {
    const root = document.documentElement;
    const get = (v) => getComputedStyle(root).getPropertyValue(v).trim();
    return {
      grid: get('--color-border-medium') || get('--color-border') || '#e2e8f0',
      text: get('--color-text-secondary') || get('--color-text-primary') || '#64748b',
    };
  })() : { grid: '#e2e8f0', text: '#64748b' };

  return (
    <div className="bg-surface rounded-xl border border-border-medium shadow-sm p-6 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-text-primary">📈 Partnership Performance</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setChartType('chemistry')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              chartType === 'chemistry'
                ? 'bg-primary text-text-inverse'
                : 'bg-surface-subtle text-text-secondary hover:bg-surface-elevated border border-border'
            }`}
          >
            Chemistry Score
          </button>
          <button
            onClick={() => setChartType('performance')}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
              chartType === 'performance'
                ? 'bg-primary text-text-inverse'
                : 'bg-surface-subtle text-text-secondary hover:bg-surface-elevated border border-border'
            }`}
          >
            Performance vs Expected
          </button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'chemistry' ? (
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
              <XAxis
                dataKey="date"
                stroke={chartTheme.text}
                tick={{ fill: chartTheme.text, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={chartTheme.text}
                tick={{ fill: chartTheme.text, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                domain={[0, 1]}
                tickFormatter={(value) => value.toFixed(1)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: chartTheme.text }} />
              <Line
                type="monotone"
                dataKey="chemistryScore"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                name="Chemistry Score"
              />
              <Line
                type="monotone"
                dataKey="winRate"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                name="Win Rate (%)"
                yAxisId="right"
              />
            </LineChart>
          ) : (
            <BarChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} />
              <XAxis
                dataKey="date"
                stroke={chartTheme.text}
                tick={{ fill: chartTheme.text, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={chartTheme.text}
                tick={{ fill: chartTheme.text, fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ color: chartTheme.text }} />
              <Bar
                dataKey="performanceVsExpected"
                fill="#8b5cf6"
                name="Performance vs Expected (%)"
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="mt-6 pt-4 border-t border-border-medium">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="text-center">
            <div className="text-lg font-bold text-info">
              {filteredData.length}
            </div>
            <div className="text-text-muted">Data Points</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-success-dark">
              {filteredData.length > 0
                ? (filteredData.reduce((sum, d) => sum + d.wins, 0) /
                   filteredData.reduce((sum, d) => sum + d.totalGames, 0) * 100).toFixed(1)
                : 0}%
            </div>
            <div className="text-text-muted">Overall Win Rate</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-info">
              {filteredData.length > 0
                ? (filteredData.reduce((sum, d) => sum + d.chemistryScore, 0) / filteredData.length).toFixed(2)
                : 0}
            </div>
            <div className="text-text-muted">Avg Chemistry</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-warning-dark">
              {filteredData.length > 0
                ? (filteredData.reduce((sum, d) => sum + d.performanceVsExpected, 0) / filteredData.length).toFixed(1)
                : 0}%
            </div>
            <div className="text-text-muted">Avg Performance</div>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-text-muted">
        {chartType === 'chemistry' ? (
          <p>
            Chemistry score shows how well you and your partner perform together over time. 
            Higher scores indicate better partnership synergy.
          </p>
        ) : (
          <p>
            Performance vs Expected shows how much better or worse you perform compared to 
            what the ELO system predicted. Positive values indicate overperformance.
          </p>
        )}
      </div>
    </div>
  );
};

export default PartnershipPerformanceChart;
